import { Box, darken, Grid, Stack, Typography } from '@mui/material';
import { FunctionComponent } from 'react';
import { useGameTheme } from '../../config/GameTheme';
import { gameConfigStore, useGameConfigSelector } from '../../store/GameConfigStore';
import { gamePlayStore, useGameplaySelector } from '../../store/GameplayStore';
import { Team } from '../../types/GameplayTypes';
import { muiStyleSheet } from '../../util/Styles';
import { SpringZoom } from '../animation/AnimatedContainers';
import { AdjustScoreButton } from './AdjustScoreDialog';
import { SegmentScore } from './SegmentScore';

export const Scores: FunctionComponent = () => {
	const roundScore = useGameplaySelector(gamePlayStore.selectors.roundPoints);

	return <Grid container>
		<Grid size={4}><TeamScore team={Team.A} /></Grid>
		<Grid size={4} sx={styles.centred}><ScoreBox score={roundScore} digits={3} /></Grid>
		<Grid size={4}><TeamScore team={Team.B} /></Grid>
	</Grid>;
};

type TeamScoreProps = {
	team: Team;
};
const TeamScore: FunctionComponent<TeamScoreProps> = (props) => {
	const { team } = props;
	const teamNames = useGameConfigSelector(gameConfigStore.selectors.teamNames);
	const controllingTeam = useGameplaySelector(gamePlayStore.selectors.controllingTeam);
	const scores = useGameplaySelector(gamePlayStore.selectors.teamScores);

	const disabled = (controllingTeam !== undefined) && (team !== controllingTeam);
	const isControlling = (controllingTeam !== undefined) && (team === controllingTeam);

	return <Stack sx={[styles.team, (team === Team.B) && styles.teamB]}>
		<SpringZoom expanded={isControlling}>
			<AdjustScoreButton team={team}>
				<ScoreBox score={scores[team]} team={team} digits={3} disabled={disabled} />
				<Typography variant='h4' sx={[styles.name, disabled && styles.nameDisabled]}>
					{teamNames[team]}
				</Typography>
			</AdjustScoreButton>
		</SpringZoom>
	</Stack >;
};

type ScoreBoxProps = {
	score: number;
	digits: number;
	team?: Team;
	disabled?: boolean;
};

export const ScoreBox: FunctionComponent<ScoreBoxProps> = (props) => {
	const teamStyle = useTeamScoreBoxOuterStyle(props.team);

	return <Stack sx={[styles.scoreBoxOuter, props.disabled ? styles.scoreBoxOuterDisabled : teamStyle]}>
		<Box sx={[styles.scoreBoxInner, styles.centred]}>
			<SegmentScore value={props.score} digits={props.digits} height={styles.segmentScore.height} />
		</Box>
	</Stack>;
};

// TODO: GameFinishedScreen could just use <ScoreBox>
export function useTeamScoreBoxOuterStyle(team: Team | undefined) {
	const theme = useGameTheme();
	const teamColor = theme.palette.game[(team === Team.A) ? 'teamA' : 'teamB'];

	return (team === undefined)
		? {
			background: `linear-gradient(180deg, ${theme.palette.game.scoreBox.start} 0%, ${theme.palette.game.scoreBox.end} 100%)`,
		}
		: {
			background: `linear-gradient(180deg, ${teamColor} 0%, ${darken(teamColor, .2)} 100%)`
		};
}

const styles = muiStyleSheet({
	centred: {
		display: 'flex',
		justifyContent: 'center'
	},
	scoreBoxOuter: theme => ({
		width: '270px',
		height: '180px',
		border: `6px solid ${theme.palette.game.white}`,
		borderRadius: '16px',
		alignItems: 'center',
		justifyContent: 'center',
		boxShadow: '0 6px 12px rgba(0,0,0,0.4)',
		overflow: 'visible',
	}),
	scoreBoxOuterDisabled: _theme => ({
		background: 'none',
		border: 'none',
		boxShadow: 'none',
	}),
	scoreBoxInner: theme => ({
		width: '210px',
		height: '120px',
		bgcolor: 'black',
		border: `4px solid ${theme.palette.game.gold}`,
		borderRadius: '8px',
	}),
	segmentScore: {
		height: '100px'
	},
	name: theme => ({
		whiteSpace: 'nowrap',
		overflow: 'visible',
		textAlign: 'center',
		color: theme.palette.game.textDark,
		marginTop: 1,
	}),
	nameDisabled: _theme => ({
		opacity: .5,
	}),
	team: {
		overflow: 'visible',
		width: '100%',
	},
	teamB: {
		alignItems: 'flex-end',
	}
});